import { ApplicationIds } from "@next-platform/packages-ts-types";

import type { DatePreset, MetricTab } from "@/pages/CreatorAnalytics/types";

export const RATING_THRESHOLDS = {
  GOOD: 0.8,
  LOW: 0.6,
  PERCENTAGE_MULTIPLIER: 100,
} as const;

export const PRESET_DAYS: Record<Exclude<DatePreset, "custom">, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

export const DEFAULT_PRESET: DatePreset = "30d";

export const ANALYTICS_VIEW_PERMISSION = "view_all_creators_analytics";

/**
 * Non-prod builds fall back to a known prod creator for Creator Analytics
 * (see resolveCreatorId in creatorAnalyticsHandler.ts), so avatar keys always
 * point at prod-only assets. Always resolve them against the prod CDN,
 * regardless of which environment the frontend is built for.
 */
export const ANALYTICS_PROD_CDN = "https://cdn.nd-api.com";

export const TAG_DISPLAY_LIMIT = 3;

export const CHART_PAGE_SIZE = 25;

export const CHART_COLORS = {
  requests: "#3b82f6",
  uniqueUsers: "#a855f7",
  depth: "#f97316",
  likes: "#22c55e",
  unlikes: "#ef4444",
  netLikes: "#3b82f6",
  rating: "#22c55e",
} as const;

export const CHART_THEME_COLORS = {
  dark: { text: "#e0e0e0", gridline: "#333333" },
  light: { text: "#333333", gridline: "#e5e7eb" },
} as const;

export const SCATTER_POINT = {
  MIN_SIZE: 8,
  SIZE_RANGE: 16,
} as const;

export const SCATTER_AXIS_LABELS = {
  hAxisDataKey: "Requests",
  vAxisDataKey: "Avg Depth",
  hAxisTitle: "Total Requests",
  vAxisTitle: "Avg Depth (messages)",
} as const;

export const NUMBER_FORMAT = {
  MILLION: 1_000_000,
  THOUSAND: 1_000,
} as const;

export const ANALYTICS_RESOURCE = {
  summary: "analytics/creator/summary",
  timeseries: "analytics/creator/timeseries",
  characters: "analytics/creator/characters",
  characterTimeseries: (characterId: string) => `analytics/creator/characters/${characterId}/timeseries`,
} as const;

export const METRIC_TABS: { key: MetricTab; label: string }[] = [
  { key: "requests", label: "Requests" },
  { key: "depth", label: "Avg Depth" },
  { key: "favorites", label: "Likes" },
  { key: "rating", label: "Rating" },
];

/** Lower bound of the analytics data window. Anything earlier is rejected by the backend. */
export const ANALYTICS_MIN_DATE_STR = "2020-01-01";

/**
 * SpicyChat guide is the canonical (and oldest) creator-analytics doc;
 * other apps reuse it until they ship a dedicated page.
 */
export const ANALYTICS_DOCS_URL_FALLBACK = "https://docs.spicychat.ai/product-guides/creators/creator-analytics";

/**
 * Per-application override for the "Help" button on the analytics
 * dashboard. Add a new entry when an app ships its own guide; missing
 * apps fall back to {@link ANALYTICS_DOCS_URL_FALLBACK}.
 */
export const ANALYTICS_DOCS_URL_BY_APPLICATION: Partial<Record<ApplicationIds, string>> = {
  [ApplicationIds.SPICYCHAT]: ANALYTICS_DOCS_URL_FALLBACK,
  [ApplicationIds.PIXELCHAT]: "https://docs.pixelchat.ai/product-guides/creators/creator-analytics",
};
